import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { useSnackbar } from '@/hooks/global-tools';
import useLayoutSize from '@/hooks/useLayoutSize';
import { SERP_BULK_DETAIL_NOT_FOUND_MESSAGE } from '@/utils/approval/serpBulkStockAdjustment';
import { DETAIL_ID_PREFIX, SHOP_CODE_ATTR } from '.';
import { SerpBulkDetailSheetBody, SerpBulkStockAdjustmentRoot } from './styles';

const LEGACY_MODAL_ID = 'showDetailModal';
const LEGACY_ONCLICK_SHOP = /showShopDetail\(\s*['"]([^'"]*)['"]\s*\)/;

/** 매장코드 → 상세 HTML. 첫 번째 것만(레거시 jQuery id 선택과 동일) */
const readShopDetails = (content: string): Map<string, string> => {
  const details = new Map<string, string>();
  if (typeof window === 'undefined') return details;
  const doc = new DOMParser().parseFromString(content, 'text/html');
  doc.querySelectorAll(`[id^="${DETAIL_ID_PREFIX}"]`).forEach((el) => {
    const code = el.id.slice(DETAIL_ID_PREFIX.length);
    if (!details.has(code)) details.set(code, el.innerHTML);
  });
  return details;
};

const readShopCode = (element: Element): string | null => {
  const code = element.getAttribute(SHOP_CODE_ATTR);
  if (code !== null) return code;
  return (
    element.getAttribute('onclick')?.match(LEGACY_ONCLICK_SHOP)?.[1] ?? null
  );
};

type DetailDialogProps = {
  html: string | null;
  onClose: () => void;
};

/** 「내역 보기」 — PC 모달, 모바일 바텀시트 */
const SerpBulkShopDetailDialog = ({ html, onClose }: DetailDialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);
  const open = html !== null;

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = html ? (
    <SerpBulkStockAdjustmentRoot>
      <SerpContentSnapshot content={html} />
    </SerpBulkStockAdjustmentRoot>
  ) : null;

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={t('상세 내역')}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <SerpBulkDetailSheetBody>{body}</SerpBulkDetailSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={t('상세 내역')} handleClose={onClose}>
      <>{body}</>
    </Modal>
  );
};

/** 작성·조회 공용 — 저장 content(레거시 문서 포함)를 그리고 매장별 「내역 보기」를 모달로 */
const SerpBulkStockAdjustmentSnapshot = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [detailHtml, setDetailHtml] = useState<string | null>(null);
  const details = useMemo(() => readShopDetails(content), [content]);

  const openDetail = useCallback(
    (code: string | null) => {
      const html = code === null ? undefined : details.get(code);
      if (html === undefined) {
        showError({ message: t(SERP_BULK_DETAIL_NOT_FOUND_MESSAGE) });
        return;
      }
      setDetailHtml(html);
    },
    [details, showError, t],
  );

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      if (element.id === LEGACY_MODAL_ID) return null;
      if (element.id.startsWith(DETAIL_ID_PREFIX)) return null;
      if (tagName === 'button' && element.closest('.td-show-detail')) {
        const code = readShopCode(element);
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => openDetail(code)}
          >
            {t('내역 보기')}
          </BoxButton>
        );
      }
      return undefined;
    },
    [openDetail, t],
  );

  return (
    <>
      <SerpBulkStockAdjustmentRoot>
        <SerpContentSnapshot content={content} transform={transform} />
      </SerpBulkStockAdjustmentRoot>
      <SerpBulkShopDetailDialog
        html={detailHtml}
        onClose={() => setDetailHtml(null)}
      />
    </>
  );
};

export default SerpBulkStockAdjustmentSnapshot;
